import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { NorthData } from '@/components/NorthRow';
import propertyImage from '@/assets/property-example.jpg';
import { useImpersonation } from '@/contexts/ImpersonationContext';

interface DashboardPage {
  norths: NorthData[];
  nextCursor: number | null;
  hasMore: boolean;
}

interface DashboardMeta {
  secondaryBrandColor: string;
  isAdmin: boolean;
}

const PAGE_SIZE = 18; // 18 = 6 complete rows in a 3-column grid, no orphaned empty slot

async function fetchDashboardPage(
  pageParam: number,
  effectiveUserId: string,
  searchQuery?: string,
  sortBy?: string,
  statusFilter?: 'all' | 'published' | 'draft' | 'processing',
  dateFilter?: 'all' | 'week' | 'month' | '3months',
  agentName?: string | null
): Promise<{ data: DashboardPage; meta: DashboardMeta }> {
  if (!effectiveUserId) throw new Error('Not authenticated');

  // Fetch metadata only on first page
  let meta: DashboardMeta = {
    secondaryBrandColor: '#1b489b',
    isAdmin: false,
  };

  if (pageParam === 0) {
    // Try to get cached metadata first (from localStorage)
    const cachedMeta = localStorage.getItem(`dashboard_meta_${effectiveUserId}`);
    if (cachedMeta) {
      try {
        const parsed = JSON.parse(cachedMeta);
        meta = parsed;
      } catch (e) {
        // Invalid cache, fetch fresh
      }
    }

    // If no cache or invalid, fetch fresh
    if (!cachedMeta || meta.secondaryBrandColor === '#1b489b') {
      const [userRoleResult, profileResult] = await Promise.all([
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', effectiveUserId)
          .single(),
        supabase
          .from('profiles')
          .select('default_secondary_color')
          .eq('user_id', effectiveUserId)
          .single(),
      ]);

      meta.isAdmin = userRoleResult.data?.role === 'admin';
      meta.secondaryBrandColor = profileResult.data?.default_secondary_color || '#1b489b';
      
      // Cache metadata for instant loading next time
      localStorage.setItem(`dashboard_meta_${effectiveUserId}`, JSON.stringify(meta));
    }
  }

  // RLS handles filtering at the database level:
  // - Team members (including team admins) see only their team's norths
  // - System admin (admin@northreports.com) sees all norths
  // Note: Team admins are NOT system admins - they only see their team's data
  // For impersonation, we filter to show the impersonated user's team norths
  const { data: { user } } = await supabase.auth.getUser();
  const isImpersonating = user?.id !== effectiveUserId;
  
  // Get user role and team info to determine filtering
  const [userRoleResult, profileResult] = await Promise.all([
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', effectiveUserId)
      .maybeSingle(),
    supabase
      .from('profiles')
      .select('team_id')
      .eq('user_id', effectiveUserId)
      .maybeSingle(),
  ]);

  const userRole = userRoleResult.data?.role;
  const isAdmin = userRole === 'admin';
  const hasTeam = !!profileResult.data?.team_id;

  // Build query with pagination
  // Show all Norths (including those still processing) - users can see them with a processing indicator
  let query = supabase
    .from('norths')
    .select('id, address, city, state, zip, status, date_created, last_refreshed, user_id, custom_agent_name, listing_agent, image_url, zillow_url, realtor_url, seller_names, seller_email, custom_agent_headshot_url, is_published, published_at, public_url_views_count, is_processing, created_by', { count: 'exact' });

  // When impersonating, filter to show ALL norths from the impersonated user's team
  if (isImpersonating && profileResult.data?.team_id) {
    // Get all user_ids from that team
    const { data: teamMembers } = await supabase
      .from('profiles')
      .select('user_id')
      .eq('team_id', profileResult.data.team_id);
    
    if (teamMembers && teamMembers.length > 0) {
      const teamUserIds = teamMembers.map(m => m.user_id);
      query = query.in('user_id', teamUserIds);
    }
  }
  // Otherwise, RLS will handle the filtering (team-based for all users including team admins)

  // Apply search filter
  if (searchQuery) {
    query = query.or(`address.ilike.%${searchQuery}%,city.ilike.%${searchQuery}%,listing_agent.ilike.%${searchQuery}%,custom_agent_name.ilike.%${searchQuery}%`);
  }

  // Apply status filter
  if (statusFilter === 'published')  query = query.eq('is_published', true).eq('is_processing', false);
  if (statusFilter === 'draft')      query = query.eq('is_published', false).eq('is_processing', false);
  if (statusFilter === 'processing') query = query.eq('is_processing', true);

  // Apply date filter
  const cutoffDays: Record<string, number> = { week: 7, month: 30, '3months': 90 };
  if (dateFilter && dateFilter !== 'all') {
    const cutoff = new Date(Date.now() - cutoffDays[dateFilter] * 86400000).toISOString();
    query = query.gte('date_created', cutoff);
  }

  // Apply agent filter — match on assigned agent name fields, not creator user_id
  if (agentName) {
    query = query.or(`custom_agent_name.ilike.${agentName},listing_agent.ilike.${agentName}`);
  }

  // Apply sorting
  const sortColumn = sortBy === 'address' ? 'address' : sortBy === 'status' ? 'status' : 'date_created';
  const sortAscending = sortBy === 'address';
  query = query.order(sortColumn, { ascending: sortAscending });

  query = query.range(pageParam * PAGE_SIZE, (pageParam + 1) * PAGE_SIZE - 1);

  const { data: northsData, error, count } = await query;

  if (error) throw error;

  // Only fetch user profiles for the current page
  const userIds = [...new Set((northsData || []).map(n => n.user_id))];
  const { data: profiles } = await supabase
    .from('profiles')
    .select('user_id, full_name')
    .in('user_id', userIds);

  const profileMap = new Map(profiles?.map(p => [p.user_id, p.full_name]) || []);

  const formattedNorths: NorthData[] = (northsData || []).map((north) => ({
    id: north.id,
    address: north.address,
    city: north.city,
    state: north.state,
    zip: north.zip,
    status: north.status,
    dateCreated: new Date(north.date_created).toLocaleDateString(),
    lastRefreshed: new Date(north.last_refreshed).toLocaleDateString(),
    createdBy: north.created_by || profileMap.get(north.user_id) || 'Unknown',
    listingAgent: north.custom_agent_name || north.listing_agent || 'N/A',
    imageUrl: north.image_url || propertyImage,
    zillowUrl: north.zillow_url,
    realtorUrl: north.realtor_url,
    sellerNames: north.seller_names,
    sellerEmail: north.seller_email,
    listingAgentPhoto: north.custom_agent_headshot_url,
    isPublished: north.is_published,
    publishedAt: north.published_at ? new Date(north.published_at).toLocaleDateString() : undefined,
    publicUrlViewsCount: north.public_url_views_count || 0,
    isProcessing: north.is_processing || false,
  }));

  const hasMore = count ? (pageParam + 1) * PAGE_SIZE < count : false;

  return {
    data: {
      norths: formattedNorths,
      nextCursor: hasMore ? pageParam + 1 : null,
      hasMore,
    },
    meta,
  };
}

export function useDashboardDataPaginated(
  searchQuery?: string,
  sortBy?: string,
  statusFilter?: 'all' | 'published' | 'draft' | 'processing',
  dateFilter?: 'all' | 'week' | 'month' | '3months',
  agentName?: string | null
) {
  const { getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();

  return useInfiniteQuery({
    // Include impersonation state in query key so it invalidates when impersonation changes
    queryKey: ['dashboard-paginated', searchQuery, sortBy, statusFilter, dateFilter, agentName, isImpersonating, impersonatedUser?.id],
    queryFn: async ({ pageParam = 0 }) => {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) throw new Error('Not authenticated');
      return fetchDashboardPage(pageParam, effectiveUserId, searchQuery, sortBy, statusFilter, dateFilter, agentName);
    },
    getNextPageParam: (lastPage) => lastPage.data.nextCursor,
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5, // 5 minutes - cache for faster loading
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when tab becomes visible
    refetchOnMount: false, // Don't refetch when component remounts
    refetchOnReconnect: false, // Don't refetch on network reconnect (use stale data)
    placeholderData: (previousData) => previousData, // Use previous data as placeholder to prevent loading state
  });
}
